import * as angular from 'angular';
import { clientSettings } from 'go-modules/models/common/client.settings';
import { Subject } from 'rxjs';
import { UADetect as UADetectClass } from 'go-modules/detect/ua-detect.service';
import type { LogoutSubscribeOptions } from '@go-modules/services/auth/interfaces/logout-subscribe-options.interface';
import type { AuthResponseSuccessDataInterface, AuthUserResponse } from '@go-modules/services/auth/interfaces/auth-response-success.interface';

export const AUTH = {
	ROLE_COOKIE_NAME: 'gr_role',
	AUTH_USER_NAME: 'gr_user',
	IS_LTI_COOKIE_NAME: 'gr_is_lti',
	EXPIRES_AT_COOKIE_NAME: 'gr_expires_at',
	HIGHEST_USER_ROLE_COOKIE_NAME: 'gr_highest_user_role'
};

export const USER_TYPES = {
	ADMIN: 'admin',
	ADMIN_WITH_BILLING: 'admin_with_billing',
	USER: 'user'
};

export const USER_ROLES = {
	ADMIN: 'admin',
	OWNER: 'owner',
	INSTRUCTOR: 'instructor',
	REVIEWER: 'reviewer',
	PRESENTER: 'presenter'
};

/**
 * AuthService setup result interface.
 */
export interface SetupResult {
	name: string;
	params: any;
	options: any;
}

/**
 * AuthService.
 *
 * Note: In the future this should be combined with the AuthService in Admin Tools when upgrading to latest Angular.
 */
export class AuthService {

	public authLogoutSubject = new Subject();

	/* @ngInject */
	constructor (
		private $http: ng.IHttpService,
		private $window: angular.IWindowService,
		private UADetect: UADetectClass,
		private $q: ng.IQService
	) {
	}

	/**
	 * Login to a user's account
	 */
	public login (username: string, password: string): ng.IHttpPromise<AuthResponseSuccessDataInterface> {
		const payload = {
			username: username || null,
			password: password || null
		};

		return this.$http.post(`${clientSettings.GoReactV2API}/users:login`, payload, {
			headers: {
				isPWA: this.UADetect.browserDetector.isPWA()
			}
		});
	}

	/**
	 * Log out the current user
	 */
	public logout (options?: LogoutSubscribeOptions): ng.IPromise<void> {
		return this.$http.post(`${clientSettings.GoReactV2API}/users:logout`, null)
			// We don't care if the endpoint fails (and it likely will if the user's token is invalid)
			// Continue with deleting all session information
			.catch(() => {})
			.then(() => this.authLogoutSubject.next(options));
	}

	/**
	 * Gets current user.
	 */
	public getCurrentUser (): ng.IHttpPromise<AuthUserResponse> {
		return this.$http.get(`${clientSettings.GoReactV2API}/users/self`);
	}

	/**
	 * LoginAs other user.
	 */
	public loginAs (userId): ng.IHttpPromise<AuthResponseSuccessDataInterface> {
		return this.$http.post(`${clientSettings.GoReactV2API}/users/${userId}/login_as`, {});
	}
}
